import React from "react";
import { Waves, Filter, Layers, Recycle, Droplets, Building2 } from "lucide-react";
import { Reveal } from "@/components/anim";

const STEPS = [
  { icon: Waves, title: "Intake", body: "Seawater or brackish water is pumped in. Protective screens minimize the intake of sea life and large debris." },
  { icon: Filter, title: "Pretreatment", body: "Water is filtered to remove sediments, organic material, bacteria and viruses before it reaches the membranes." },
  { icon: Layers, title: "Desalination", body: "Water is forced through the membrane vessels under pressure — removing more than 99% of the salt and dissolved minerals." },
  { icon: Recycle, title: "Effluent", body: "Advanced diffuser technology reintroduces the residual effluent into the ocean in a safe, responsible and environmentally sound manner — rapidly diluting it to protect marine life and habitats." },
  { icon: Droplets, title: "Post-Treatment", body: "Water is remineralized — minerals are added back to match the taste and quality of the existing supply." },
  { icon: Building2, title: "To the Public", body: "Fresh, drought-proof water is stored and released into the municipal and industrial water system." },
];

const PROCESS_LABELS = [
  { name: "Intake", num: 1, x: "11%", pillY: "0%", anchor: "15.5%" },
  { name: "Pretreatment", num: 2, x: "22%", pillY: "58%", anchor: "43%", below: true },
  { name: "Membrane", num: 3, x: "41%", pillY: "0%", anchor: "23%" },
  { name: "Post-Treatment", num: 5, x: "63%", pillY: "14%", anchor: "35.5%" },
  { name: "To the Public", num: 6, x: "79%", pillY: "6%", anchor: "24.5%" },
  { name: "Effluent", num: 4, x: "48%", pillY: "84%", anchor: "66%", below: true },
];

export default function WhatDesalinationIs() {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="bg-white py-28 md:py-40">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="text-brand-teal text-xs font-bold uppercase tracking-[0.25em] mb-4">How It Works</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-heading text-4xl md:text-6xl font-medium tracking-tight text-ink leading-none max-w-3xl">
              What desalination is.
            </h2>
            <p className="text-ink-soft max-w-md text-base leading-relaxed">
              From salt water to tap water — how we convert seawater and brackish water into clean, drinkable fresh
              water through proven reverse-osmosis technology.
            </p>
          </div>
        </Reveal>

        {/* Process illustration with labels */}
        <Reveal delay={0.05}>
          <div className="mt-12 border border-brand-navy/10 bg-white p-4 md:p-6">
            <div className="relative">
              <img
                src="/assets/desal-process.png"
                alt="Desalination process — from salt water intake to fresh water at the tap"
                className="w-full h-auto"
                data-testid="process-illustration"
              />
              <div className="hidden lg:block absolute inset-0 pointer-events-none">
                {PROCESS_LABELS.map((l) => (
                  <React.Fragment key={l.name}>
                    <span
                      className="absolute -translate-x-1/2 whitespace-nowrap bg-brand-navy text-white text-[10px] lg:text-xs font-bold uppercase tracking-[0.12em] px-2 md:px-3 py-1 md:py-1.5 shadow-sm"
                      style={{ left: l.x, top: l.pillY }}
                      data-testid={`process-label-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.name}
                    </span>
                    <span
                      className="absolute -translate-x-1/2 w-px bg-brand-navy/50"
                      style={
                        l.below
                          ? { left: l.x, top: l.anchor, height: `calc(${l.pillY} - ${l.anchor})` }
                          : { left: l.x, top: `calc(${l.pillY} + 26px)`, height: `calc(${l.anchor} - ${l.pillY} - 26px)` }
                      }
                    />
                  </React.Fragment>
                ))}
              </div>
              {/* Mobile / tablet: small numbered markers keyed to the step cards below */}
              <div className="lg:hidden absolute inset-0 pointer-events-none">
                {PROCESS_LABELS.map((l) => (
                  <span
                    key={l.name}
                    className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center rounded-full bg-brand-navy text-white text-[10px] font-bold w-5 h-5 ring-2 ring-white shadow"
                    style={{ left: l.x, top: l.anchor }}
                    data-testid={`process-marker-${l.num}`}
                  >
                    {l.num}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Numbered step descriptions */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-navy/15 border border-brand-navy/15">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={i} delay={i * 0.05} className="bg-white">
                <div className="group h-full p-7 flex flex-col hover:bg-brand-navy transition-colors duration-300">
                  <div className="flex items-center justify-between">
                    <span className="font-accent italic text-brand-red text-3xl group-hover:text-brand-teal transition-colors">
                      0{i + 1}
                    </span>
                    <Icon className="text-brand-navy group-hover:text-brand-teal transition-colors" size={26} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-4 font-heading text-ink text-xl tracking-tight group-hover:text-white transition-colors">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-ink-soft text-sm md:text-base leading-relaxed group-hover:text-white/70 transition-colors">
                    {s.body}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Reverse osmosis membrane explainer */}
        <Reveal delay={0.05}>
          <div className="mt-12 bg-bg-dark p-8 md:p-12 border border-brand-navy/10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-brand-teal text-xs font-bold uppercase tracking-[0.25em] mb-4">How the Membrane Works</p>
                <h3 className="font-heading text-2xl md:text-3xl text-white tracking-tight leading-tight">
                  Reverse osmosis, simply put.
                </h3>
                <p className="mt-4 text-white/60 text-base md:text-lg leading-relaxed">
                  Salt water is forced under pressure through layers of a superfine semipermeable membrane. Fresh water
                  passes through, while salts and other dissolved contaminants are filtered out and left behind.
                </p>
              </div>
              <MembraneDiagram />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function MembraneDiagram() {
  return (
    <div className="w-full" data-testid="membrane-diagram">
      <svg viewBox="0 0 520 200" className="w-full h-auto" role="img" aria-label="Reverse osmosis membrane diagram">
        <text x="8" y="24" fill="#00E5FF" fontSize="13" fontFamily="Manrope, sans-serif" letterSpacing="2" fontWeight="700">SALT WATER</text>
        {[[40, 70], [80, 110], [55, 150], [110, 60], [95, 165], [130, 120]].map(([x, y], k) => (
          <g key={k} transform={`translate(${x},${y})`}>
            <rect width="12" height="12" fill="none" stroke="#c8102e" strokeWidth="2" transform="rotate(12)" />
          </g>
        ))}
        <line x1="30" y1="185" x2="200" y2="185" stroke="#00B4D8" strokeWidth="2" />
        <polygon points="200,185 192,181 192,189" fill="#00B4D8" />
        <rect x="235" y="20" width="26" height="160" fill="url(#memGrad)" opacity="0.9" />
        {[...Array(16)].map((_, k) => (
          <line key={k} x1="235" y1={26 + k * 10} x2="261" y2={26 + k * 10} stroke="#0B1320" strokeWidth="1" opacity="0.35" />
        ))}
        <text x="248" y="200" fill="#ffffff" fontSize="11" fontFamily="Manrope, sans-serif" textAnchor="middle" opacity="0.7">MEMBRANE</text>
        <line x1="275" y1="100" x2="470" y2="100" stroke="#ffffff" strokeWidth="3" />
        <polygon points="480,100 466,92 466,108" fill="#ffffff" />
        <text x="360" y="80" fill="#ffffff" fontSize="15" fontFamily="Manrope, sans-serif" letterSpacing="2" fontWeight="700" textAnchor="middle">FRESH WATER</text>
        {[[330, 125], [380, 135], [430, 120]].map(([x, y], k) => (
          <circle key={k} cx={x} cy={y} r="4" fill="#00B4D8" />
        ))}
        <defs>
          <linearGradient id="memGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#00B4D8" />
            <stop offset="1" stopColor="#1a3a6b" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}
